import React from 'react';
import {
  useGetOne,
} from 'react-admin';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Chip,
  Card,
  CardContent,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  CircularProgress,
  Alert,
  Avatar,
} from '@mui/material';
import {
  Close as CloseIcon,
  Assignment as CompletedFormsIcon,
  DirectionsCar as DirectionsCarIcon,
  GetApp as DownloadIcon,
  PictureAsPdf as PictureAsPdfIcon,
  LocationOn as LocationOnIcon,
} from '@mui/icons-material';
import { downloadFileAuthenticated } from '../../utils/downloadFile';

// Helper functions for the completed form dialog
const handleDownloadPdf = async (formId: number, pdfFileId?: number) => {
  try {
    const endpoint = pdfFileId
      ? `/completed-forms/${formId}/pdf`
      : `/completed-forms/${formId}/make-pdf`;
    await downloadFileAuthenticated(endpoint, `completed-form-${formId}.pdf`);
  } catch (error) {
    console.error('PDF download failed:', error);
    alert('Failed to download PDF. Please try again or contact support.');
  }
};

// Completed Form Dialog Component
export const CompletedFormDialog = ({ open, onClose, formId }: {
  open: boolean;
  onClose: () => void;
  formId: number | null;
}) => {
  const { data: rawForm, isLoading: loading, error } = useGetOne<any>(
    'completed-forms',
    { id: formId || 0 },
    { enabled: !!formId }
  );

  // Parse formDefinition if it's a string
  const form = React.useMemo(() => {
    if (!rawForm) return null;
    const parsedForm = { ...rawForm };
    if (typeof parsedForm.formDefinition === 'string') {
      try {
        parsedForm.formDefinition = JSON.parse(parsedForm.formDefinition);
      } catch (e) {
        console.error('Failed to parse formDefinition:', e);
      }
    }
    return parsedForm;
  }, [rawForm]);

  // Group form fields by category
  const groupedFields = React.useMemo(() => {
    const groups: Record<string, any[]> = {
      'Vehicle Selection': [],
      'General Information': [],
      'Inspection Items': [],
      'Images': [],
      'Signatures': [],
    };

    if (!form?.formDefinition?.fields) return groups;

    form.formDefinition.fields.forEach((field: any) => {
      if (!field.label || (field.userValue === undefined && field.userValue === null)) {
        return;
      }

      if (field.label === "Operator's Name:" || field.label === "Operator's Name") {
        return;
      }

      if (field.type === 'Images') {
        groups['Images'].push(field);
      } else if (field.type === 'SignatureImage') {
        groups['Signatures'].push(field);
      } else if (field.type === 'EquipmentLookup') {
        groups['Vehicle Selection'].push(field);
      } else if (field.type === 'Information') {
        // Skip information fields
      } else {
        const hasDefectsInOptions = field.options && Array.isArray(field.options) &&
          field.options.some((opt: string) => opt.toLowerCase().includes('defect'));

        const hasDefectsInValue = field.userValue && typeof field.userValue === 'string' &&
          field.userValue.toLowerCase().includes('defect');

        const inspectionKeywords = [
          'brake', 'tire', 'wheel', 'suspension', 'steering',
          'exhaust', 'horn', 'lamp', 'reflector', 'windshield',
          'wiper', 'washer', 'coupling', 'cargo', 'securement',
          'dangerous goods', 'emergency equipment', 'safety device',
          'fuel system', 'electric', 'hydraulic', 'glass', 'mirror',
          'heater', 'defroster', 'cab', 'driver seat', 'driver controls',
          'defects repaired'
        ];

        const labelLower = field.label.toLowerCase();
        const isInspectionItem = hasDefectsInOptions || hasDefectsInValue ||
          inspectionKeywords.some(keyword => labelLower.includes(keyword));

        if (isInspectionItem) {
          groups['Inspection Items'].push(field);
        } else {
          groups['General Information'].push(field);
        }
      }
    });

    return groups;
  }, [form]);

  // Component for form field display
  const InfoCard = ({ icon, title, children }: { icon: React.ReactNode; title: string; children: React.ReactNode }) => (
    <Card sx={{ height: '100%', borderRadius: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Avatar sx={{ bgcolor: 'primary.light', width: 32, height: 32 }}>
            {icon}
          </Avatar>
          <Typography variant="h6" fontWeight="600">
            {title}
          </Typography>
        </Box>
        {children}
      </CardContent>
    </Card>
  );

  const FieldValue = ({ label, value }: { label: string; value: any }) => (
    <Box sx={{ mb: 2 }}>
      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {label}
      </Typography>
      <Typography variant="body1" sx={{ mt: 0.5 }}>
        {value === null || value === undefined ? 'N/A' : String(value)}
      </Typography>
    </Box>
  );

  const renderFieldValue = (value: any): string => {
    if (value === null || value === undefined) return 'N/A';
    if (typeof value === 'object') return JSON.stringify(value, null, 2);
    return String(value);
  };

  if (!open || !formId) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            {form?.form?.name || 'Completed Form'} - #{formId}
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ maxHeight: '80vh', p: 3 }}>
        {loading && (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error">{error.message || 'An error occurred'}</Alert>
        )}

        {form && (
          <>
            {/* Info Cards */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} md={6}>
                <InfoCard icon={<CompletedFormsIcon fontSize="small" />} title="Form Information">
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <FieldValue label="Form Type" value={form.form?.name || 'Unknown Form'} />
                      <FieldValue label="Form ID" value={`#${form.id}`} />
                      <FieldValue label="Started" value={new Date(form.startedAt).toLocaleString()} />
                      <FieldValue label="Completed" value={new Date(form.completedAt).toLocaleString()} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FieldValue label="Completed By" value={form.user?.name || 'N/A'} />
                      <FieldValue label="Email" value={form.user?.email || 'N/A'} />
                      {form.operatorName && (
                        <FieldValue label="Operator's Name" value={form.operatorName} />
                      )}
                    </Grid>
                  </Grid>

                  {/* PDF Section */}
                  <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      PDF Document
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                      <Chip
                        icon={form.pdfFileId ? <PictureAsPdfIcon /> : <DownloadIcon />}
                        label="Download PDF"
                        size="small"
                        color={form.pdfFileId ? "success" : "primary"}
                        variant="outlined"
                        clickable
                        onClick={() => handleDownloadPdf(form.id, form.pdfFileId)}
                      />
                    </Box>
                  </Box>
                </InfoCard>
              </Grid>

              {form.equipment && (
                <Grid item xs={12} md={6}>
                  <InfoCard icon={<DirectionsCarIcon fontSize="small" />} title="Vehicle/Equipment">
                    <FieldValue
                      label="Vehicle"
                      value={`${form.equipment.manufacturedYear || ''} ${form.equipment.make || ''} ${form.equipment.model || ''}`.trim() || form.equipment.name}
                    />
                    <FieldValue label="Type" value={form.equipment.type || 'N/A'} />
                    {form.equipment.licensePlate && (
                      <FieldValue label="License" value={form.equipment.licensePlate} />
                    )}
                    {form.equipment.vin && (
                      <FieldValue label="VIN" value={form.equipment.vin} />
                    )}
                  </InfoCard>
                </Grid>
              )}

              {(form.location || form.customLocationLabel) && (
                <Grid item xs={12} md={6}>
                  <InfoCard icon={<LocationOnIcon fontSize="small" />} title="Location">
                    {form.location ? (
                      <>
                        <FieldValue label="Location" value={form.location.label} />
                        {form.location.address && (
                          <FieldValue label="Address" value={form.location.address} />
                        )}
                      </>
                    ) : (
                      <>
                        <FieldValue label="Location" value={form.customLocationLabel} />
                        {form.customLocationLatitude != null && form.customLocationLongitude != null && (
                          <FieldValue
                            label="Coordinates"
                            value={`${form.customLocationLatitude}, ${form.customLocationLongitude}`}
                          />
                        )}
                      </>
                    )}
                  </InfoCard>
                </Grid>
              )}
            </Grid>

            {/* Form Data */}
            <Paper sx={{ p: 3, borderRadius: 2 }}>
              <Typography variant="h6" fontWeight="600" gutterBottom sx={{ mb: 3 }}>
                Form Data
              </Typography>

              {/* General Information */}
              {groupedFields['General Information'].length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" fontWeight="500" gutterBottom sx={{ mb: 2 }}>
                    General Information
                  </Typography>
                  <Grid container spacing={2}>
                    {groupedFields['General Information'].map((field: any, index: number) => (
                      <Grid item xs={12} sm={6} md={4} key={index}>
                        <FieldValue
                          label={field.label}
                          value={renderFieldValue(field.userValue)}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}

              {/* Inspection Items */}
              {groupedFields['Inspection Items'].length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" fontWeight="500" gutterBottom sx={{ mb: 2 }}>
                    Inspection Items
                  </Typography>
                  <Grid container spacing={2}>
                    {groupedFields['Inspection Items'].map((field: any, index: number) => (
                      <Grid item xs={12} sm={6} md={3} key={index}>
                        <Paper elevation={0} sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                          <FieldValue
                            label={field.label}
                            value={renderFieldValue(field.userValue)}
                          />
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}
            </Paper>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};